import * as React from 'react'

const GLOBAL = window.document

const KEYDOWN = 'keydown'
const KEYUP = 'keyup'

const DELIMITER = '+'

/**
 * Component that allows use key combinations
 * to handle callbacks
 * Listeners will be added to this.props.children by default
 *
 * Three props are expected, 2 mandatory (keyMap, handlers)
 * and two optional (global, autofocus)
 *
 * keyMap<Object>
 * Use event.code to describe key map, can obtain array of combinations
 * example:
 * keyMap = {'undo': ['Control+KeyZ'], 'redo: 'Control+ShiftLeft+KeyZ'}
 *
 * handlers<Object>
 * example:
 * handlers: {undo: () => callUndo(), redo: (e) => e.preventDefault() && callRedo()}
 *
 * global<Boolean> "false" by default
 * Add listeners to document node if global=true,
 * without that flag listener will be setted to children
 * example:
 * <Wrapper keyMap={keyMap} handlers={handlers} global>{children}</Wrapper>
 *
 * autofocus<Boolean> "false" by default
 * Set focus to wrapper
 * example:
 * <Wrapper keyMap={keyMap} handlers={handlers} autofocus>{children}</Wrapper>
 */
export default class HotKeys extends React.Component {
  static defaultProps = {
    keyMap: {},
    toggleKeyMap: {}
  }

  constructor(props) {
    super(props)

    this._pressedKeys = {}
    this._subscriber = {
      eventTypes: [KEYDOWN, KEYUP],
      callback: {}
    }
  }

  initializeCallbacks = () => {
    this._subscriber.callback = {
      [KEYDOWN]: event => {
        if (this._pressedKeys[event.code]) return
        this._pressedKeys[event.code] = event

        const checkCombination = combination =>
          combination.split(DELIMITER).every(k => this._pressedKeys[k])
        const isPressedCheck = keys =>
          Array.isArray(keys)
            // eslint-disable-next-line lodash/prefer-lodash-method
            ? keys.some(k => checkCombination(k))
            : checkCombination(keys)

        Object.entries(this.props.keyMap).forEach(([method, keys]) => {
          const combinationPressed = isPressedCheck(keys)

          if (combinationPressed) {
            this.callMethod(method, event)
            delete this._pressedKeys[event.code]
          }
        })

        Object.entries(this.props.toggleKeyMap).forEach(([method, keys]) => {
          const combinationPressed = isPressedCheck(keys)

          if (combinationPressed) {
            this.callMethod(method, event, 'down')
          }
        })
      },
      [KEYUP]: event => {
        Object.entries(this.props.toggleKeyMap).forEach(([method, keys]) => {
          if (event.code === keys) {
            this.callMethod(method, event, 'up')
          }
        })

        this._pressedKeys[event.code] && delete this._pressedKeys[event.code]
      }
    }
  }

  callMethod = (method, event, type = null) => {
    const isContainEvent =
      (this.props.handlers[method] &&
        typeof this.props.handlers[method] === 'function') ||
      (this.props.handlers[method] &&
        this.props.handlers[method][type] &&
        typeof this.props.handlers[method][type] === 'function')

    if (isContainEvent) {
      // Stop propagation by default if it nesting component
      !this.props.global && event.stopPropagation()

      type
        ? this.props.handlers[method][type](event)
        : this.props.handlers[method](event)
    }
  }

  componentDidMount() {
    this.initializeCallbacks()

    const elem = this.props.global ? GLOBAL : this.wrapper
    const capturing = !this.props.global
    this._subscriber.eventTypes.forEach(eventType =>
      elem.addEventListener(
        eventType,
        this._subscriber.callback[eventType],
        capturing
      )
    )

    if (this.props.autofocus) {
      requestAnimationFrame(() => elem.focus())
    }
  }

  componentWillUnmount() {
    const elem = this.props.global ? GLOBAL : this.wrapper
    const capturing = !this.props.global
    this._subscriber.eventTypes.forEach(eventType =>
      elem.removeEventListener(
        eventType,
        this._subscriber.callback[eventType],
        capturing
      )
    )
  }

  render() {
    return this.props.global ? null : (
      <div
        tabIndex="0"
        ref={elem => (this.wrapper = elem)}
        className={this.props.className}
        style={{ outline: 'none', ...this.props.style }}
      >
        {this.props.children}
      </div>
    )
  }
}
